import React, { useState, useRef, useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { USER_PROFILE, USER_ROLE } from '../../services/auth';
import { useBranches } from '../../context/branches';

const CustomerForm = ({ customer, dataErrors, onSave, onCancel, handleDownloadFromServer }) => {

  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role == 'admin');
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
  const { branches, loading: branch_loading, refresh: refreshBranches } = useBranches({ reload: false, params: { status: 'active', paginate: false } });

  const default_mis_fields = [
    { key: "trip_code", name: "trip_code", column_name: "Booking ID", searchable: true, is_default: true },
    { key: "booking_date", name: "booking_date", column_name: "Booking Date", searchable: false, is_default: true },
    { key: "booking_time", name: "booking_time", column_name: "Booking Time", searchable: false, is_default: true },
    { key: "expected_pickup_date", name: "expected_pickup_date", column_name: "Requirement Date", searchable: false, is_default: true },
    { key: "expected_pickup_time", name: "expected_pickup_time", column_name: "Requirement Time", searchable: false, is_default: true },
    { key: "requested_by", name: "requested_by", column_name: "Requestor Name", searchable: false, is_default: true },
    { key: "type", name: "type", column_name: "Department", searchable: false, is_default: true },
    { key: "vehicle_type", name: "vehicle_type", column_name: "Vehicle Type", searchable: false, is_default: true },
    { key: "customer_name", name: "customer_name", column_name: "Customer", searchable: false, is_default: true },
    { key: "client_name", name: "client_name", column_name: "Client", searchable: false, is_default: true },
    { key: "consignment_hwb", name: "consignment_hwb", column_name: "HWB", searchable: false, is_default: true },
    { key: "consignment_tms_id", name: "consignment_tms_id", column_name: "TMS ID", searchable: false, is_default: true },
    { key: "from_location", name: "from_location", column_name: "Origin", searchable: false, is_default: true },
    { key: "to_location", name: "to_location", column_name: "Destination", searchable: false, is_default: true },
    { key: "client_delivery_type", name: "client_delivery_type", column_name: "Local/Nonlocal", searchable: false, is_default: true },
    { key: "vendor_name", name: "vendor_name", column_name: "Vendor Name", searchable: false, is_default: true },
    { key: "vehicle_capacity", name: "vehicle_capacity", column_name: "FT", searchable: false, is_default: true },
    { key: "placement_pickup_date", name: "placement_pickup_date", column_name: "Placement Date", searchable: false, is_default: true },
    { key: "placement_pickup_time", name: "placement_pickup_time", column_name: "Placement Time", searchable: false, is_default: true },
    { key: "vehicle_number", name: "vehicle_number", column_name: "Vehicle Number", searchable: false, is_default: true },
    { key: "driver_name", name: "driver_name", column_name: "Driver Contact Name", searchable: false, is_default: true },
    { key: "driver_phone", name: "driver_phone", column_name: "Driver Contact Number", searchable: false, is_default: true },
    { key: "actual_in_pickup_date", name: "actual_pickup_date", column_name: "IN Date Pickup Location", searchable: false, is_default: true },
    { key: "actual_in_pickup_time", name: "actual_pickup_time", column_name: "IN Time Pickup Location", searchable: false, is_default: true },
    { key: "actual_out_pickup_date", name: "actual_out_pickup_date", column_name: "Out Date Pickup Location", searchable: false, is_default: true },
    { key: "actual_out_pickup_time", name: "actual_out_pickup_time", column_name: "Out Time Pickup Location", searchable: false, is_default: true },
    { key: "actual_in_delivery_date", name: "actual_delivery_date", column_name: "IN Date Delivery Location", searchable: false, is_default: true },
    { key: "actual_in_delivery_time", name: "actual_delivery_time", column_name: "IN Time Delivery Location", searchable: false, is_default: true },
    { key: "actual_out_delivery_date", name: "actual_out_delivery_date", column_name: "Out Date Delivery Location", searchable: false, is_default: true },
    { key: "actual_out_delivery_time", name: "actual_out_delivery_time", column_name: "Out Time Delivery Location", searchable: false, is_default: true },
    { key: "consignment_remarks", name: "consignment_remarks", column_name: "Consignment Note", searchable: false, is_default: true },
    { key: "consignment_quantity", name: "consignment_quantity", column_name: "No. of Ctns/Plt", searchable: false, is_default: true },
    { key: "consignment_weight", name: "consignment_weight", column_name: "weight", searchable: false, is_default: true },
    { key: "freight_charges", name: "freight_charges", column_name: "Freight", searchable: false, is_default: true },
    { key: "detention_charges", name: "detention_charges", column_name: "Detention Charges", searchable: false, is_default: true },
    { key: "parking_charges", name: "parking_charges", column_name: "Parking Charges", searchable: false, is_default: true },
    { key: "loading_unloading_charges", name: "loading_unloading_charges", column_name: "Loading & Unloading Charges", searchable: false, is_default: true },
    { key: "toll_charges", name: "toll_charges", column_name: "Toll Charges", searchable: false, is_default: true },
    { key: "total_charges", name: "total_charges", column_name: "Total", searchable: false, is_default: true },
  ];

  const [formData, setFormData] = useState({
    id: customer?.id || 0,
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    short_code: customer?.short_code || '',
    branch: customer?.branch || '',
    branch_id: showBranches ? (customer?.branch_id || 0) : profile.branch,
    address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    pincode: customer?.pincode || '',
    loading_charge: customer?.loading_charge || 0.0,
    unloading_charge: customer?.unloading_charge || 0.0,
    toll_charge: customer?.toll_charge || 0.0,
    freight_charge: customer?.freight_charge || 0.0,
    detention_charge: customer?.detention_charge || 0.0,
    parking_charge: customer?.parking_charge || 0.0,
    sla_contract: customer?.sla_contract || null,
    custom_fields: customer?.custom_fields || [],
    mis_fields: customer?.mis_fields || default_mis_fields,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [selectedMISField, setSelectedMISField] = useState("all");
  const [misSearch, setMisSearch] = useState('');
  const [customSearch, setCustomSearch] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    refreshBranches();
  }, []);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'pincode') {
      value = value.replace(/\D/g, '').slice(0, 6);
    } else if ([
      'name',
      'email',
      'short_code',
      'address_line1',
      'address_line2',
      'city',
      'state',
    ].includes(name)) {
      value = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, sla_contract: file }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    // if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.email) newErrors.email = 'Email is required';
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Invalid pincode';
    return newErrors;
  };

  const cleanupData = (data) => {
    const customer = { ...data };
    if (customer.id == 0 &&  'customer_code' in customer) {
      delete customer.customer_code;
    }
    return customer;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationErrors([]);
    const errors = validate();
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    const customer = cleanupData(formData);
    onSave(customer);
  };

  const handleMISFieldSelect = (e) => {
    const selected = e.target.value;
    setSelectedMISField(selected);
  };

  const handleDownloadLocalFile = (fileObject) => {
    const url = URL.createObjectURL(fileObject);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileObject.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = (e) => {
    e.preventDefault();
    const file = formData['sla_contract'];
    if (file instanceof File) {
      handleDownloadLocalFile(file);
    } else if (typeof file === "string") {
      handleDownloadFromServer(formData);
    }
  };

  const onMisFieldChecked = (field, val) => {
    const mis_fields = formData.mis_fields;
    if (val) {
      mis_fields.push({
        key: field.name,
        name: field.name,
        column_name: field.name,
        searchable: false,
        is_default: false
      });
    } else {
      let idx = -1;
      for (let i = 0; i < mis_fields.length; i++) {
        if (mis_fields[i].key == field.name) {
          idx = i;
          break;
        }
      }
      if (idx > -1) {
        delete mis_fields[idx];
      }
    }
    setFormData({...formData, mis_fields});
  };

  const updateMISFieldOnCustomFieldNameUpdate = (idx, old) => {
    const custom_fields = formData.custom_fields;
    const updatedField = custom_fields[idx];
    if (updatedField.report_field) {
      const mis_fields = formData.mis_fields;
      for (let idx = 0; idx < mis_fields.length; idx++) {
        if (mis_fields[idx].key == old?.name) {
          mis_fields[idx].key = updatedField.name;
          break;
        }
      }
      setFormData({...formData, custom_fields});
    }
  };

  const onAddMISField = () => {
    let fields = []
    if (selectedMISField == 'all') {
      fields.push(...get_remaining_mis_fields());
    } else if (selectedMISField == 'others') {
      fields.push({ key: "", name: "", column_name: "", searchable: false, is_default: false });
    } else {
      fields.push(...get_remaining_mis_fields().filter(field => field.key == selectedMISField));
    }
    setFormData((prev) => ({
      ...prev,
      mis_fields: [
        ...(prev.mis_fields || []),
        ...fields
      ],
    }))
  };

  const get_remaining_mis_fields = () => {
    return default_mis_fields
      .filter((field) => !(formData.mis_fields || []).some(
        (added) => added.key === field.key
      ));
  };

  const updateBranch = (index) => {
    setFormData({ ...formData, branch: branches[index], branch_id: branches[index].id });
  }

  const misRows = useMemo(() => {
    const keyword = misSearch.trim().toLowerCase();
    return (formData.mis_fields || [])
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => {
        if (!keyword) return true;
        return [field?.name, field?.key, field?.column_name]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(keyword));
      });
  }, [formData.mis_fields, misSearch]);

  const customRows = useMemo(() => {
    const keyword = customSearch.trim().toLowerCase();
    return (formData.custom_fields || [])
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => {
        if (!keyword) return true;
        return [field?.name, field?.field_type]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(keyword));
      });
  }, [formData.custom_fields, customSearch]);

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white p-4 rounded shadow space-y-4 uppercase [&_input[type='text']]:uppercase [&_textarea]:uppercase [&_select]:uppercase"
    >
      {/* Top Row: Name, Contact, Email */}
      <div className="grid md:grid-cols-4 gap-4">
        {/* Branch */}
        {showBranches && (
            <div>
              <label className="block text-sm">Branch</label>
              <select name="branch" value={formData.branch?.name}
                onChange={(e) => updateBranch(e.target.selectedIndex - 1)}
                className="w-full border px-2 py-1 rounded">
                <option value="">-- Select --</option>
                {branches.map((branch) => <option key={branch.id} value={branch.name}>{branch.name}</option>)}
              </select>
              {dataErrors.branch && <p className="text-red-500 text-sm mt-1">{dataErrors.branch[0]}</p>}
              {dataErrors.branch_id && <p className="text-red-500 text-sm mt-1">{dataErrors.branch_id[0]}</p>}
            </div>
        )}
        <div>
          <label className="block text-sm font-medium">Name</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full text-sm"
          />
          {dataErrors.name && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.name[0]}</p>
          )}
          {validationErrors.name && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium">Contact</label>
          <input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full text-sm"
          />
          {dataErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.phone[0]}</p>
          )}
          {validationErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium">Email</label>
          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full text-sm"
          />
          {dataErrors.email && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.email[0]}</p>
          )}
          {validationErrors.email && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
          )}
        </div>
      </div>

      {/* Location Section */}
      <div>
        <h2 className="text-sm font-semibold mb-2">Location</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-600 mb-1">Short Code</label>
            <input
              name="short_code"
              value={formData.short_code}
              onChange={handleChange}
              placeholder="Short Code"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.short_code && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.short_code[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Address Line 1</label>
            <input
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              placeholder="Address Line 1"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.address_line1 && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.address_line1[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Address Line 2</label>
            <input
              name="address_line2"
              value={formData.address_line2}
              onChange={handleChange}
              placeholder="Address Line 2"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.address_line2 && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.address_line2[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">City</label>
            <input
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.city && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.city[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">State</label>
            <input
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="State"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.state && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.state[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Pincode</label>
            <input
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              placeholder="Pincode"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.pincode && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.pincode[0]}</p>
            )}
            {validationErrors.pincode && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.pincode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Billing Info */}
      {/* <div>
        <h2 className="text-sm font-semibold mb-2">Billing Info</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-600 mb-1">Loading Charge</label>
            <input
              name="loading_charge"
              value={formData.loading_charge}
              onChange={handleChange}
              placeholder="Loading Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.loading_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.loading_charge[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Unloading Charge</label>
            <input
              name="unloading_charge"
              value={formData.unloading_charge}
              onChange={handleChange}
              placeholder="Unloading Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.unloading_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.unloading_charge[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Toll Charge</label>
            <input
              name="toll_charge"
              value={formData.toll_charge}
              onChange={handleChange}
              placeholder="Toll Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.toll_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.toll_charge[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Freight Charge</label>
            <input
              name="freight_charge"
              value={formData.freight_charge}
              onChange={handleChange}
              placeholder="Freight Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.freight_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.freight_charge[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Detention Charge</label>
            <input
              name="detention_charge"
              value={formData.detention_charge}
              onChange={handleChange}
              placeholder="Detention Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.detention_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.detention_charge[0]}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1">Parking Charge</label>
            <input
              name="parking_charge"
              value={formData.parking_charge}
              onChange={handleChange}
              placeholder="Parking Charges"
              className="border rounded px-3 py-2 w-full text-sm"
            />
            {dataErrors.parking_charge && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.parking_charge[0]}</p>
            )}
          </div>
        </div>
      </div> */}

      {/* MIS Fields Section */}
      <div className="border border-slate-200 rounded-xl bg-slate-50/60 p-4 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">MIS FIELDS</h2>
            <p className="text-xs text-slate-500 mt-1">
              MANAGE REPORT COLUMNS AND DISPLAY LABELS
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <select
              onChange={handleMISFieldSelect}
              value={selectedMISField}
              className="border border-slate-300 rounded-md px-3 py-2 text-xs bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="all">ALL DEFAULT FIELDS</option>
              <option value="others">ADD CUSTOM EMPTY FIELD</option>
              <option disabled>────────────</option>
              {get_remaining_mis_fields().map((field, idx) => (
                <option key={idx} value={field.key}>
                  {field.column_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onAddMISField}
              className="bg-[#293686] text-white text-xs px-4 py-2 rounded-md hover:bg-[#1F2968]"
            >
              + ADD FIELD
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">
            TOTAL: {(formData.mis_fields || []).length}
          </span>
          <input
            type="text"
            value={misSearch}
            onChange={(e) => setMisSearch(e.target.value)}
            placeholder="SEARCH MIS FIELDS"
            className="w-full max-w-xs border border-slate-300 rounded-md px-3 py-2 text-xs bg-white"
          />
        </div>

        {(!formData.mis_fields || formData.mis_fields.length === 0) ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
            NO MIS FIELDS ADDED YET.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <div className="hidden md:grid md:grid-cols-12 gap-3 text-[11px] font-semibold text-slate-600 px-3 py-2 bg-slate-100 sticky top-0 z-10">
              <div className="md:col-span-4">FIELD KEY</div>
              <div className="md:col-span-5">COLUMN LABEL</div>
              <div className="md:col-span-1">TYPE</div>
              <div className="md:col-span-2 text-right">ACTIONS</div>
            </div>

            <div className="max-h-[360px] overflow-y-auto p-2 space-y-2">
              {misRows.length === 0 && (
                <div className="text-xs text-slate-500 px-2 py-3">
                  NO RESULTS FOUND FOR "{misSearch}".
                </div>
              )}
              {misRows.map(({ field, index: fieldIndex }) => (
                <div
                  key={`${field.key || field.name || 'mis'}-${fieldIndex}`}
                  className="grid md:grid-cols-12 gap-3 items-center border border-slate-200 rounded-lg p-3 bg-white"
                >
                <div className="md:col-span-4">
                  <label className="md:hidden text-[11px] text-slate-500 mb-1 block">FIELD KEY</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="FIELD KEY"
                    value={field.name}
                    disabled={field.is_default}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => {
                        const updated = [...prev.mis_fields];
                        updated[fieldIndex].name = val;
                        if (!updated[fieldIndex].is_default) {
                          updated[fieldIndex].key = val;
                        }
                        return { ...prev, mis_fields: updated };
                      });
                    }}
                    className={`border rounded-md px-3 py-2 text-sm w-full ${
                      field.is_default ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"
                    }`}
                  />
                </div>

                <div className="md:col-span-5">
                  <label className="md:hidden text-[11px] text-slate-500 mb-1 block">COLUMN LABEL</label>
                  <input
                    type="text"
                    name="column_name"
                    placeholder="COLUMN LABEL"
                    value={field.column_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => {
                        const updated = [...prev.mis_fields];
                        updated[fieldIndex].column_name = val;
                        return { ...prev, mis_fields: updated };
                      });
                    }}
                    className="border rounded-md px-3 py-2 text-sm w-full"
                  />
                </div>

                <div className="md:col-span-1">
                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-[10px] font-semibold ${
                      field.is_default
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {field.is_default ? 'DEFAULT' : 'CUSTOM'}
                  </span>
                </div>

                <div className="md:col-span-2 flex md:justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => {
                        const deletedField = prev.mis_fields[fieldIndex];
                        const updatedMISFields = prev.mis_fields.filter((_, i) => i !== fieldIndex);
                        let updatedCustomFields = [...prev.custom_fields];
                        if (!deletedField.is_default) {
                          updatedCustomFields = updatedCustomFields.map((cf) =>
                            cf.name === deletedField.key
                              ? { ...cf, report_field: false }
                              : cf
                          );
                        }
                        return {
                          ...prev,
                          mis_fields: updatedMISFields,
                          custom_fields: updatedCustomFields,
                        };
                      });
                    }}
                    className="text-red-600 border border-red-200 hover:bg-red-50 rounded-md px-3 py-1.5 text-xs font-semibold"
                  >
                    DELETE
                  </button>
                </div>
              </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Custom Fields Section */}
      <div className="border border-slate-200 rounded-xl bg-slate-50/60 p-4 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">CUSTOM FIELDS</h2>
            <p className="text-xs text-slate-500 mt-1">
              DEFINE CUSTOMER-SPECIFIC FIELD STRUCTURE
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                custom_fields: [
                  ...(prev.custom_fields || []),
                  { name: "", field_type: "Text", mandatory: false, report_field: false, searchable: false },
                ],
              }))
            }
            className="bg-[#293686] text-white text-xs px-4 py-2 rounded-md hover:bg-[#1F2968]"
          >
            + ADD FIELD
          </button>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">
            TOTAL: {(formData.custom_fields || []).length}
          </span>
          <input
            type="text"
            value={customSearch}
            onChange={(e) => setCustomSearch(e.target.value)}
            placeholder="SEARCH CUSTOM FIELDS"
            className="w-full max-w-xs border border-slate-300 rounded-md px-3 py-2 text-xs bg-white"
          />
        </div>

        {(formData.custom_fields || []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
            NO CUSTOM FIELDS ADDED YET.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <div className="hidden md:grid md:grid-cols-12 gap-3 text-[11px] font-semibold text-slate-600 px-3 py-2 bg-slate-100 sticky top-0 z-10">
              <div className="md:col-span-4">FIELD NAME</div>
              <div className="md:col-span-2">TYPE</div>
              <div className="md:col-span-2 text-center">MANDATORY</div>
              <div className="md:col-span-2 text-center">MIS FIELD</div>
              <div className="md:col-span-2 text-right">ACTIONS</div>
            </div>
            <div className="max-h-[360px] overflow-y-auto p-2 space-y-2">
              {customRows.length === 0 && (
                <div className="text-xs text-slate-500 px-2 py-3">
                  NO RESULTS FOUND FOR "{customSearch}".
                </div>
              )}
              {customRows.map(({ field, index: fieldIndex }) => (
                <div
                  key={`${field.name || 'custom'}-${fieldIndex}`}
                  className="grid md:grid-cols-12 gap-3 items-center border border-slate-200 rounded-lg p-3 bg-white"
                >
                  <div className="md:col-span-4">
                    <label className="md:hidden text-[11px] text-slate-500 mb-1 block">FIELD NAME</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="FIELD NAME"
                      value={field.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => {
                          const updated = [...prev.custom_fields];
                          const oldValue = { ...updated[fieldIndex] };
                          updated[fieldIndex].name = val;
                          updateMISFieldOnCustomFieldNameUpdate(fieldIndex, oldValue);
                          return { ...prev, custom_fields: updated };
                        });
                      }}
                      className="border rounded-md px-3 py-2 text-sm w-full"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="md:hidden text-[11px] text-slate-500 mb-1 block">TYPE</label>
                    <select
                      name="field_type"
                      value={field.field_type}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => {
                          const updated = [...prev.custom_fields];
                          updated[fieldIndex].field_type = val;
                          return { ...prev, custom_fields: updated };
                        });
                      }}
                      className="border rounded-md px-3 py-2 text-sm w-full"
                    >
                      <option value="Text">TEXT</option>
                      <option value="Number">NUMBER</option>
                      <option value="Date">DATE</option>
                      <option value="Time">TIME</option>
                      <option value="Datetime">DATETIME</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 flex md:justify-center">
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={field.mandatory}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setFormData((prev) => {
                            const updated = [...prev.custom_fields];
                            updated[fieldIndex].mandatory = val;
                            return { ...prev, custom_fields: updated };
                          });
                        }}
                      />
                      MANDATORY
                    </label>
                  </div>

                  <div className="md:col-span-2 flex md:justify-center">
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={field.report_field}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setFormData((prev) => {
                            const updated = [...prev.custom_fields];
                            updated[fieldIndex].report_field = val;
                            return { ...prev, custom_fields: updated };
                          });
                          onMisFieldChecked(field, val);
                        }}
                      />
                      MIS FIELD
                    </label>
                  </div>

                  <div className="md:col-span-2 flex md:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          custom_fields: prev.custom_fields.filter((_, i) => i !== fieldIndex),
                        }));
                      }}
                      className="text-red-600 border border-red-200 hover:bg-red-50 rounded-md px-3 py-1.5 text-xs font-semibold"
                    >
                      DELETE
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customer Contract Upload */}
      <div>
        <label className="block text-sm font-medium mb-1">Customer Contract</label>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm"
          >
            Choose File
          </button>
          <span className="text-sm text-gray-600 truncate max-w-xs">
            {formData.sla_contract ? formData.sla_contract.name : 'No file chosen'}
          </span>
          <input
            name="sla_contract"
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
          />
          {dataErrors.sla_contract && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.sla_contract[0]}</p>
          )}
          {formData.sla_contract && (
          <>
            {/* <button
              onClick={() => handleView(formData.sla_contract)}
              className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
              title="View"
            >
              <EyeIcon className="w-5 h-5" />
            </button> */}
            <button
              onClick={(e) => handleDownload(e)}
              className="text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-50"
              title="Download"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
          </>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-800 px-4 py-2 rounded text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded text-sm"
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;
